import { GeometryType, ItineraryType, type ItineraryDTO, type ItineraryLegDTO } from "../types/dtos/api/itinerary/itinerary.dto";
import { LegProvider } from "../types/itinerary/leg-provider.enum";
import { LegType } from "../types/itinerary/leg-type.enum";
import { type Itinerary, type Leg } from "../types/ItineraryPlanner";

interface Location {
    "lat": number;
    "lon": number;
}

interface ItineraryResponse {
    "hints": Record<string, number>;
    "info":  Record<string, unknown>;
    "paths": Array<{
        "distance": number;
        "time":     number;
        "points":   string;
        "price":    number;
    }>;
}

export function transformItineraryToMyCarCard( data: ItineraryResponse, startDate: number, startLocation: Location, endLocation: Location ): ItineraryDTO | null {
    if ( data.paths == null || data.paths.length === 0 ) {
        return null;
    }

    const path = data.paths[0];

    const startTime = startDate;
    const endTime = startTime + path.time;

    // transform date from timestamp to this : 2024-11-27T12:01:00+01:00

    const startDateISO = new Date( startTime ).toISOString();
    const endDateISO = new Date( endTime ).toISOString();

    // Creating a single Leg from the path data directly
    const leg: ItineraryLegDTO = {
        "id":         `mycar_${ startTime }_${ endTime }`,
        "type":       LegType.car,
        "provider":   LegProvider.motis,
        "providerId": null,
        "mode":       "mycar",
        "duration":   path.time,
        "distance":   path.distance,
        "from":       {
            "name":          "Départ",
            "location":      startLocation,
            "time":          startDateISO,
            "scheduledTime": startDateISO,
            "modes":         [],
        },
        "to": {
            "name":          "Arrivée",
            "location":      endLocation,
            "time":          endDateISO,
            "scheduledTime": endDateISO,
            "modes":         [],
        },
        "geometry": {
            "path": path.points,
            "type": GeometryType.TWO_DIMENSIONAL,
        },
        "route": {
            "type":      "MYCAR",
            "shortName": "MYCAR",
            "longName":  "My Car",
        },
        "bikesAllowed": false,
    };

    return {
        "id":          `mycar_itinerary_${ startTime }_${ endTime }`,
        "finalizedAt": endDateISO,
        "timestamp":   startTime,
        "finalized":   true,
        "duration":    path.time,
        "startTime":   startDateISO,
        "endTime":     endDateISO,
        "price":       path.price,
        "legs":        [ leg ], // An array containing just the single leg
        "type":        ItineraryType.CAR,
    };
}

export function transformItineraries( data: ItineraryResponse, startDate: number ): Itinerary[] {
    if ( data.paths.length === 0 ) {
        console.error( "Invalid path data" );

        return [];
    }

    return data.paths.map( ( path ) => {
        const startTime = startDate;
        const endTime = startTime + path.time;

        const leg: Leg = {
            "mode":     "mycar",
            startTime,
            endTime,
            "duration": path.time,
            "distance": path.distance,
            "realTime": false,
            "route":    {
                "type":      "MYCAR",
                "shortName": "MYCAR",
            },
        };

        return {
            startTime,
            endTime,
            "duration": path.time,
            "price":    path.price,
            "legs":     [ leg ],
            "points":   path.points,
        };
    } );
}

/**
 * Convertit un type d'itineraire en priorité
 * @param type type d'itinéraire
 */
export function typeToOrder( type: ItineraryType ): number {
    switch ( type ) {
        case ItineraryType.MOTIS:
            return 1;

        case ItineraryType.BLABLADAILY:
            return 2;

        case ItineraryType.BLABLACAR:
            return 3;

        case ItineraryType.CAR:
            return 4;

        default:
            return Number.MAX_SAFE_INTEGER;
    }
}

/**
 * Drops heavy leg fields not used by pricing (CFF / rent bike / carpool) to shrink the pricing POST body.
 * Keeps the same leg shape as search so the backend and merge keys stay aligned with full itineraries.
 */
function stripLegForPricing( leg: ItineraryLegDTO ): ItineraryLegDTO {
    const { geometry, timeMarkers, intermediateStops, ...rest } = leg;
    void geometry;
    void timeMarkers;
    void intermediateStops;

    return rest;
}

export function stripItinerariesForPricingRequest( itineraries: ItineraryDTO[] ): ItineraryDTO[] {
    return itineraries.map( ( itinerary ) => ( {
        "id":          itinerary.id,
        "finalizedAt": itinerary.finalizedAt,
        "timestamp":   itinerary.timestamp,
        "finalized":   itinerary.finalized,
        "duration":    itinerary.duration,
        "startTime":   itinerary.startTime,
        "endTime":     itinerary.endTime,
        "type":        itinerary.type,
        "legs":        itinerary.legs.map( stripLegForPricing ),
        ...itinerary.price !== null && itinerary.price !== undefined ? { "price": itinerary.price } : {},
        ...itinerary.walkTime !== undefined ? { "walkTime": itinerary.walkTime } : {},
        ...itinerary.legIndexForId !== undefined ? { "legIndexForId": itinerary.legIndexForId } : {},
    } ) );
}
